import React, { useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { REAL_TIME_ROUTES, REPORT_STATUS, BACKEND_APIS } from '@constants'
import { AppButtonIcon, AppLinearProgress } from '@components'
import { batchIdStatusList, batchTableCellData } from 'Pages/Batch/Page/@signals'
import { formatDate, getExcel } from '@ui-seeker/utils'
import { dateFormatReceiver } from '@utils'
import { reportSearchesModalId } from '@signals'
import { useComputed } from '@preact/signals-react'


export default function historyColumn() {

	return {
		name: 'id_batch', label: 'Status',
		options: {
			filter: false,
			customBodyRowRenderLite: (idBatch, row) => <CellContent idBatch={idBatch} row={row}/>,
		},
	}
}


function CellContent({ idBatch, row }) {
	const rowData = useComputed(() => ({ ...row, ...batchTableCellData.value[idBatch] }))
	const isRunning = useComputed(() => rowData.value?.status === REPORT_STATUS.RUNNING)

	useEffect(() => {
		if (isRunning.value) {
			batchIdStatusList.value = [...batchIdStatusList.value, idBatch]
		}
	}, [idBatch])

	return <Box sx={{
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	}}>
		{rowData.value.status !== REPORT_STATUS.RUNNING && rowData.value?.status === REPORT_STATUS.STOPPED
			? <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
				<Typography variant="caption">
					{formatDate(rowData.value?.timestamp_update_tz, {
						isString: true,
						format: 'dd MMM yyyy HH:mm',
						receivedFormat: dateFormatReceiver(rowData.value?.timestamp_update_tz)
					})}
				</Typography>
				<Typography variant="body1">
					<IconSeeker icon="hand" sx={{ color: 'error.main' }}/> Stopped
				</Typography>
			</Box>
			: <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: .5 }}>
				<Typography variant="body3">
					{formatDate(rowData.value?.timestamp_update_tz, {
						isString: true,
						receivedFormat: dateFormatReceiver(rowData.value?.timestamp_update_tz),
						format: 'dd MMM yyyy',
					})}
				</Typography>
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					{formatDate(rowData.value?.timestamp_update_tz, {
						isString: true,
						receivedFormat: dateFormatReceiver(rowData.value?.timestamp_update_tz),
						format: 'HH:mm:ss',
					})}
				</Typography>
			</Box>
		}
		<Box sx={{ cursor: !rowData.value?.generating ? 'pointer' : '', width: 1 }}
			 onClick={() => {
				 if (rowData.value?.status !== REPORT_STATUS.GENERATING) {
					 reportSearchesModalId.value = rowData.value?.id_batch
				 }
			 }}>
			{isRunning.value &&
				<Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
					{rowData.value?.generating ? 'Generating' : 'Click progress bar to view details'}
				</Typography>}

			{(isRunning.value && !rowData.value?.generating) &&
				<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
					{rowData.value?.completeTasksString || 0}/{rowData.value?.totalTasksString || 0}
				</Typography>}

			{isRunning.value &&
				<AppLinearProgress actual={rowData.value?.completeTasks} total={rowData.value?.totalTasks}
								   generating={rowData.value?.generating}/>}
		</Box>

		<Box sx={{ width: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
				<IconSeeker
					sx={{
						color: 'warning.main',
						m: 0.5,
						fontSize: '1.3rem',
					}}
					iconWeight="regular"
					icon="triangle-exclamation"
					size="large"
					tooltip="Search Errors"/>
				<Typography>{row?.errorTasks}</Typography>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
				<IconSeeker
					sx={{
						color: 'error.main',
						m: 0.5,
						fontSize: '1.3rem',
					}}
					iconWeight="regular"
					icon="calendar-circle-exclamation"
					size="large"
					tooltip="N/A Results"
				/>
				<Typography>{row?.naTasks}</Typography>
			</Box>
			{row.export && row?.exported && row?.export_url &&
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
					<AppButtonIcon
						sx={{
							color: 'success.main',
							fontSize: '1.3rem',
							variant: 'solid',
						}}
						iconWeight="regular"
						icon="cloud-arrow-down"
						size="large"
						tooltip="Download Export"
						onClick={e => {
							window.open(row?.export_url)
							if (e.ctrlKey) e.preventDefault()
						}}
						onAuxClick={e => {
							if (e.button === 1) {
								window.open(row?.export_url)
								e.preventDefault()
							}
						}}
					/>
					<Typography sx={{ height: 19.5 }}/>
				</Box>}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
				<AppButtonIcon
					sx={{
						color: 'primary.main',
						p: '4px',
						pr: '4px',
						fontSize: '1.3rem',
						variant: 'solid',
					}}
					iconWeight="solid"
					icon="clock-rotate-left"
					size="large"
					tooltip="View Historical"
					onClick={e => {
						window.open(`${REAL_TIME_ROUTES.HISTORY_PATH}?idBatch=${row.id_batch}`)
						if (e.ctrlKey) e.preventDefault()
					}}
					onAuxClick={e => {
						if (e.button === 1) {
							window.open(`${REAL_TIME_ROUTES.HISTORY_PATH}?idBatch=${row.id_batch}`)
							e.preventDefault()
						}
					}}
				/>
				<Typography sx={{ height: 19.5 }}/>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
				<AppButtonIcon
					sx={{
						color: 'success.dark',
						fontSize: '1.3rem',
					}}
					iconWeight="regular"
					icon="file-excel"
					size="large"
					tooltip="Download Batch Excel"
					onClick={() => getExcel(`${BACKEND_APIS.BATCH}/excel`, {idBatch: row.id_batch})}
				/>
				<Typography sx={{ height: 19.5 }}/>
			</Box>
		</Box>

	</Box>
}